import {
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
  TableOutlined,
} from '@ant-design/icons'
import {
  Button,
  Checkbox,
  Input,
  Modal,
  Popconfirm,
  Select,
  Table,
  Tooltip,
} from 'antd'
import dayjs from 'dayjs'
import Papa from 'papaparse'
import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useHistory, useLocation } from 'react-router-dom'
import {
  FoodSafetyHeaderRow,
  FoodSafetyHeroBand,
  FoodSafetyPageTitle,
} from 'src/components/food-safety/FoodSafetyPageChrome'
import {
  FOOD_SAFETY_SECONDARY_TEXT,
  FOOD_SAFETY_SURFACE,
} from 'src/constants/foodSafety'
import NoDataScreen from 'src/components/shared/no-data-screen/NoDataScreen'
import FadeIn from 'src/components/styled/FadeIn'
import ShareChangeWithGroup from 'src/screens/menu-builder/components/ShareChangeWithGroup'
import {
  antdTableHorizontalScroll,
  stickyActionsColumn,
} from 'src/components/styled/horizontalScroll'
import { ollieFabPageClearanceCss } from 'src/components/ollie/ollieFabPlacement'
import Loading from 'src/components/styled/Loading'
import GrowthEngineFollowUpStrip from 'src/components/growthEngine/GrowthEngineFollowUpStrip'
import GrowthEngineFeatureEmptyState from 'src/components/growthEngine/GrowthEngineFeatureEmptyState'
import GrowthEngineProgressAskOllie from 'src/components/growthEngine/GrowthEngineProgressAskOllie'
import { resolveProgressAskOllie } from 'src/utils/growthEngineProgressAskOllie'
import GrowthEngineFirstSuccessCompletion from 'src/components/growthEngine/GrowthEngineFirstSuccessCompletion'
import useGrowthEngineFeatureGuidance from 'src/hooks/useGrowthEngineFeatureGuidance'
import useGrowthEngineSuggestedData from 'src/hooks/useGrowthEngineSuggestedData'
import useJustAchievedFirstSuccess from 'src/hooks/useJustAchievedFirstSuccess'
import {
  pickProductForFirstPar,
  parseGrowthEngineSuggestedProduct,
  suggestedParQtyForProgressHandoff,
} from 'src/utils/growthEngineSuggestedProduct'
import { shouldHideFirstTryMarketingBanners } from 'src/utils/growthEngineOneJobPage'
import {
  getFirstSuccessCompletion,
  getParLevelsEmptyCopy,
  resolveParNeedProductPath,
  shouldShowFirstSuccessCompletion,
  shouldShowParLevelsGuidedEmpty,
  suggestedParLevelEditValue,
} from 'src/utils/growthEngineRecommendation'
import { bindGuidedEmptySuggestedActions } from 'src/utils/growthEngineSuggestedData'
import OllieOrderingEntry from 'src/components/ollie/OllieOrderingEntry'
import PermissionBlockScreen from 'src/components/shared/PermissionBlockScreen'
import useIsBlocked from 'src/hooks/useIsBlocked'
import { VAT_RATE } from 'src/config'
import { RESPONSIVE_TABLE_SCROLL } from 'src/utils/responsiveTable'
import {
  SHOPPING_LIST_OPTIONAL_COLUMN_DEFS,
  filterShoppingListVisibleColumns,
  hiddenShoppingListColumnsFromWidth,
  listShoppingListOptionalColumns,
  loadShoppingListHiddenColumns,
  saveShoppingListHiddenColumns,
  toggleShoppingListHiddenColumn,
} from 'src/utils/shoppingListColumns'
import { mapShoppingListCsvRow } from 'src/utils/parseShoppingListCsvRow'
import {
  deleteAllVenueProducts,
  deleteVenueProduct,
  editVenueProduct,
  fetchVenueProducts,
  uploadVenueProductsCSV,
} from 'src/features/VenueProductsSlice'
import {
  RejectedNotification,
  SuccessNotification,
} from 'src/notifications/SuccessNotification'
import ChangedPricesTable from 'src/screens/venue-products/ChangedPricesTable'
import EditVenueProductModal from 'src/screens/venue-products/EditVenueProductModal'
import {
  filterShoppingListProducts,
  shoppingListProductTypeLabel,
} from 'src/utils/shoppingListSearch'
import {
  countVenueProductsWithParLevel,
  formatParLevelUsage,
  getParLevelForProduct,
  PAR_LEVEL_LIMIT_ERROR_CODE,
  previewParLevelSave,
} from 'src/utils/parLevelPaywall'
import {
  PAR_READINESS_SET_PARS_CTA,
  PAR_READINESS_OTP_BODY,
  PAR_READINESS_OTP_HEADLINE,
  PAR_READINESS_OTP_STOCK_CTA,
  formatParReadinessBanner,
  pickProductToSetPar,
  PAR_SET_PARS_ROW_HINT,
} from 'src/utils/parLevelReadiness'
import ParLevelUpgradeModalContent from 'src/utils/ParLevelUpgradeModalContent'
import { VENUE_PRODUCTS_BANNER_PAR_LEVEL_BULLET } from 'src/utils/parLevelMarketingCopy'
import { ParLevelFieldTooltipContent } from 'src/utils/parLevelFieldTooltip'
import {
  ColumnHeaderWithTooltip,
  DefaultQtyColumnTooltipContent,
  ExcludeFromGrossProfitColumnHeader,
  ZoneColumnTooltipContent,
} from 'src/utils/venueProductFieldTooltips'
import {
  GROWTH_ENGINE_GUIDED_LANE_REASONS,
  emitGrowthEngineGuidedLaneProgress,
} from 'src/utils/growthEngineNudgeRefresh'
import {
  trackParLevelPaywallTriggered,
  trackParLevelSaveIfChanged,
} from 'src/utils/parLevelAnalytics'
import { notifyParLevelPaywallSales } from 'src/utils/parLevelPaywallNotify'
import { buildVenueProductSavePayload } from 'src/utils/venueProductSavePayload'
import { isVenueProductExcludedFromGrossProfit } from 'src/utils/excludeFromGrossProfit'
import { promptExcludeFromGrossProfitDuplicateTitle } from 'src/utils/promptExcludeFromGrossProfitDuplicateTitle'
import { getVenueCustomImageUrl } from 'src/utils/venueProductImage'
import { useWindowWidth } from 'src/utils/useWindowWidth'
import wordCaps from 'src/utils/wordCaps'
import { getActiveSpecialPrice } from 'src/utils/priceFunctions'
import { formatGbp } from 'src/utils/formatGbp'
import styled, { ThemeContext } from 'styled-components/macro'
import BasketFunctionality from '../basketFunctionality/BasketFunctionality'
import DownloadProductsCsv from './DownloadProductsCsv'
import ComingImg from 'src/assets/images/imageComingGrn.png'
import ProductImage from 'src/components/shared/ProductImage'
import 'src/components/shared/inline-table-edit/inlineTableEdit.css'
import useVenueProductInlineEdit from 'src/screens/venue-products/useVenueProductInlineEdit'
import ExcludeFromGrossProfitTick from 'src/screens/venue-products/ExcludeFromGrossProfitTick'
import { persistVenueProductExcludeFromGrossProfitToggle } from 'src/screens/venue-products/venueProductInlineEdit'
import ReportProductIssueButton from 'src/components/productIssue/ReportProductIssueButton'
import { PRODUCT_ISSUE_SOURCE } from 'src/utils/productIssue'

const { Option } = Select

const parLevelColumnHeader = (
  <ColumnHeaderWithTooltip
    label='Par Level'
    tooltip={<ParLevelFieldTooltipContent />}
    nowrap
  />
)

const zoneColumnHeader = (
  <ColumnHeaderWithTooltip
    label='Zone'
    tooltip={<ZoneColumnTooltipContent />}
  />
)

const defaultQtyColumnHeader = (
  <ColumnHeaderWithTooltip
    label='Default qty'
    tooltip={<DefaultQtyColumnTooltipContent />}
  />
)

const excludeFromGpColumnHeader = <ExcludeFromGrossProfitColumnHeader />

/** Non-Error abort keeps the edit modal open without CRA's error overlay. */
const EDIT_PRODUCT_SAVE_ABORTED = { editProductSaveAborted: true }

const VenueProducts = props => {
  const [shareList, setShareList] = useState(false)
  const venueId = props.match.params.venueId
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const suggestedProduct = parseGrowthEngineSuggestedProduct(location.search)
  const guidedSuccess = useGrowthEngineFeatureGuidance({
    venueId,
    surface: 'par_levels',
    featureId: 'par_levels',
    product: suggestedProduct?.title,
  })
  const account = useSelector(state => state.auth.account)
  const products = useSelector(state => state.venueProducts.products)
  const isLoading = useSelector(state => state.venueProducts.isLoading)
  const isAdmin = account && account.isAdmin
  const isTFRAdmin = account && account.TFRAdmin
  const isJustAdmin = account && account.JUSTAdmin
  const isGreenAdmin = account && account.GREENAdmin
  const { isPricesHidden, dataplusIntegration, usageLimits } = useSelector(
    state => state.venue.venue,
  )
  const currentPlan = usageLimits?.currentPlan ?? 'Free'
  const parLevelUsageText = formatParLevelUsage(
    countVenueProductsWithParLevel(products),
    currentPlan,
  )

  const showParLevelUpgradeModal = useCallback(
    (source = 'client', productContext = null) => {
      trackParLevelPaywallTriggered({
        venueId,
        source,
        plan: currentPlan,
      })

      notifyParLevelPaywallSales({
        venueId,
        source,
        platform: 'dashboard',
        venueProductId: productContext?._id ?? null,
        productTitle: productContext?.title ?? null,
      })

      Modal.confirm({
        title: 'Upgrade required',
        zIndex: 1100,
        content: <ParLevelUpgradeModalContent />,
        okText: 'Upgrade',
        cancelText: 'Not now',
        onOk: () => {
          history.push(`/v/${venueId}/subscriptions`)
        },
      })
    },
    [currentPlan, history, venueId],
  )

  const _file = useRef(null)
  const windowWidth = useWindowWidth()
  const [columnPickerOpen, setColumnPickerOpen] = useState(false)
  const [hiddenColumnKeys, setHiddenColumnKeys] = useState(() =>
    loadShoppingListHiddenColumns(venueId),
  )

  useEffect(() => {
    setHiddenColumnKeys(loadShoppingListHiddenColumns(venueId))
  }, [venueId])
  const theme = useContext(ThemeContext)
  const isMobile = windowWidth <= theme.breakpoints.lg
  const showPriceColumn = (!isPricesHidden && isAdmin) || !isAdmin
  const showSpecialPriceColumn =
    (isAdmin || isTFRAdmin || isJustAdmin || isGreenAdmin) && !isPricesHidden
  const shoppingListPickerRows = listShoppingListOptionalColumns(
    SHOPPING_LIST_OPTIONAL_COLUMN_DEFS,
    {
      showPrice: showPriceColumn,
      showSpecialPrice: showSpecialPriceColumn,
      showGln: Boolean(dataplusIntegration),
    },
  )
  const pickerHiddenKeys = Array.isArray(hiddenColumnKeys)
    ? hiddenColumnKeys
    : hiddenShoppingListColumnsFromWidth(
        SHOPPING_LIST_OPTIONAL_COLUMN_DEFS,
        windowWidth,
      )
  const handleToggleShoppingListColumn = columnKey => {
    const next = toggleShoppingListHiddenColumn(pickerHiddenKeys, columnKey)
    saveShoppingListHiddenColumns(venueId, next)
    setHiddenColumnKeys(next)
  }

  const [searchInput, setSearchInput] = useState(
    () => suggestedProduct?.title || '',
  )
  const [searchTerm, setSearchTerm] = useState(
    () => suggestedProduct?.title || '',
  )
  const [selectedSupplierTitle, setSelectedSupplierTitle] = useState('')
  const [selectedProductType, setSelectedProductType] = useState('')
  const [bannerDismissed, setBannerDismissed] = useState(false)
  const [bannerExpanded, setBannerExpanded] = useState(false)
  const [parSetRowHint, setParSetRowHint] = useState(false)

  useEffect(() => {
    if (products.length === 0) dispatch(fetchVenueProducts(venueId))
  }, [dispatch, venueId])

  const [paginationPage, setPaginationPage] = useState(1)
  const [isCSVLoading, setCSVLoading] = useState(false)
  const resetPagination = useCallback(() => setPaginationPage(1), [])

  const applyShoppingListSearch = useCallback(
    (term = searchInput) => {
      setSearchTerm(String(term || '').trim())
      resetPagination()
    },
    [resetPagination, searchInput],
  )

  const upload = () => {
    setCSVLoading(true)
    if (!_file.current.files[0]) {
      setCSVLoading(false)
      alert('Choose a file first')
      return
    }

    Papa.parse(_file.current.files[0], {
      header: true,
      skipEmptyLines: true,
      // Normalise the chunk so Papa uses the real header row, regardless of
      // any extra “Shopping List …”, Unnamed columns, or "nan" rows that
      // spreadsheet tools might insert above it.
      beforeFirstChunk: chunk => {
        const lines = chunk.split(/\r\n|\n|\r/)

        // Find the line that looks like the actual header row
        const headerIndex = lines.findIndex(line => {
          const lower = line.toLowerCase()
          return (
            lower.includes('product') &&
            lower.includes('zone') &&
            lower.includes('default') &&
            lower.includes('qty')
          )
        })

        if (headerIndex >= 0) {
          return lines.slice(headerIndex).join('\n')
        }

        return chunk
      },
      complete: async function (results) {
        const newData = results.data.map(mapShoppingListCsvRow)
        await dispatch(uploadVenueProductsCSV(newData, venueId, setCSVLoading))
      },
      error: function () {
        setCSVLoading(false)
        alert(
          "Selected file could not be parsed. Are you sure it's a CSV file?",
        )
        // console.log(err)
      },
    })
  }

  const [editingProduct, setEditingProduct] = useState(null)

  const handleEditProductSave = useCallback(async () => {
    if (!editingProduct) {
      throw EDIT_PRODUCT_SAVE_ABORTED
    }

    const preview = previewParLevelSave({
      venueProducts: products,
      venueProductId: editingProduct._id,
      newParLevel: editingProduct.parLevel,
      plan: currentPlan,
    })

    if (preview.blocked) {
      showParLevelUpgradeModal('client', editingProduct)
      throw EDIT_PRODUCT_SAVE_ABORTED
    }

    const existingProduct = products.find(
      product => product._id === editingProduct._id,
    )
    const previousParLevel = getParLevelForProduct(existingProduct)
    const excludeChanged =
      isVenueProductExcludedFromGrossProfit(existingProduct) !==
      isVenueProductExcludedFromGrossProfit(editingProduct)

    if (excludeChanged) {
      const confirmed = await promptExcludeFromGrossProfitDuplicateTitle({
        product: editingProduct,
        products,
        excluding: isVenueProductExcludedFromGrossProfit(editingProduct),
      })
      if (!confirmed) {
        throw EDIT_PRODUCT_SAVE_ABORTED
      }
    }

    const result = await dispatch(
      editVenueProduct(buildVenueProductSavePayload(editingProduct, venueId)),
    )

    if (result === PAR_LEVEL_LIMIT_ERROR_CODE) {
      showParLevelUpgradeModal('server', editingProduct)
      throw EDIT_PRODUCT_SAVE_ABORTED
    }

    if (!result) {
      RejectedNotification({
        message: 'Could not save product',
        description:
          'Please try again. If the problem persists, contact support.',
      })
      throw EDIT_PRODUCT_SAVE_ABORTED
    }

    SuccessNotification({
      message: 'Product updated',
      description: `"${result.title || editingProduct.title}" was saved`,
    })

    const nextParLevel = getParLevelForProduct(editingProduct)
    trackParLevelSaveIfChanged({
      venueId,
      venueProductId: editingProduct._id,
      previousParLevel,
      newParLevel: nextParLevel,
    })
    if (previousParLevel !== nextParLevel && venueId) {
      emitGrowthEngineGuidedLaneProgress(
        venueId,
        GROWTH_ENGINE_GUIDED_LANE_REASONS.PAR_SAVED,
      )
    }
    setEditingProduct(null)
  }, [
    currentPlan,
    dispatch,
    editingProduct,
    products,
    showParLevelUpgradeModal,
    venueId,
  ])

  const allTruthyZones = products
    .map(product => wordCaps(product.zone).trim())
    .filter(zone => Boolean(zone))
  const uniqueProductZones = [...new Set(allTruthyZones)]

  const allTruthyTypes = products
    .map(product => shoppingListProductTypeLabel(product))
    .filter(type => Boolean(type))
  const uniqueProductTypes = [...new Set(allTruthyTypes)].sort((left, right) =>
    left.localeCompare(right),
  )

  const allTruthySuppliers = products
    .filter(product => Boolean(product.supplierTitle))
    .map(product => product.supplierTitle)

  const uniqueProductSuppliers = [...new Set(allTruthySuppliers)].sort(
    (left, right) => String(left).localeCompare(String(right)),
  )

  const productsWithoutNulls = products.filter(product =>
    product.product ? product : false,
  )
  const parReadinessBanner = formatParReadinessBanner(productsWithoutNulls)

  const searchProductsWithoutNulls = filterShoppingListProducts(
    productsWithoutNulls,
    {
      searchTerm,
      supplierTitle: selectedSupplierTitle,
      productType: selectedProductType,
    },
  )
  const shoppingListSearchActive = Boolean(
    String(searchTerm || '').trim() ||
      selectedSupplierTitle ||
      selectedProductType,
  )
  const shoppingListResultBits = [
    selectedSupplierTitle || null,
    selectedProductType || null,
  ].filter(Boolean)

  const handleOnClickButton = () => {
    _file.current.click()
  }

  const { renderInlineEditableCell, startInlineEdit } =
    useVenueProductInlineEdit({
      venueId,
      currentPlan,
      showParLevelUpgradeModal,
    })

  const showParGuidedEmpty = shouldShowParLevelsGuidedEmpty({
    products: productsWithoutNulls,
    guidedSuccess,
    loading: isLoading,
  })
  const parEmpty = getParLevelsEmptyCopy({
    products: productsWithoutNulls,
    emptyState: guidedSuccess?.emptyState,
  })
  const hideProductsTableChrome =
    productsWithoutNulls.length === 0 && showParGuidedEmpty && !isAdmin
  const hideFirstTryMarketing = shouldHideFirstTryMarketingBanners(
    showParGuidedEmpty ||
      productsWithoutNulls.length === 0 ||
      Boolean(suggestedProduct?.fromProgress),
  )
  const firstSuccessCompletion = getFirstSuccessCompletion(guidedSuccess)
  const hasAnyPars = productsWithoutNulls.some(product => {
    const n = Number(product?.parLevel)
    return Number.isFinite(n) && n > 0
  })
  const [justAchievedFirstPar, setJustAchievedFirstPar] =
    useJustAchievedFirstSuccess({
      loading: isLoading,
      isEmpty: !hasAnyPars,
      hasOutcome: Boolean(firstSuccessCompletion?.outcome),
    })
  const showFirstSuccessCompletion = shouldShowFirstSuccessCompletion({
    justAchieved: justAchievedFirstPar,
    guidedSuccess,
  })
  const suggested = useGrowthEngineSuggestedData({
    venueId,
    featureId: 'par_levels',
    items: guidedSuccess?.suggestedData?.items,
  })
  const progressParHandoffDoneRef = useRef(false)

  const beginFirstPar = item => {
    if (parEmpty.needsProductFirst) {
      const path = resolveParNeedProductPath(venueId)
      if (path) history.push(path)
      return
    }
    const { product, pick, searchTitle } = pickProductForFirstPar({
      item,
      suggestedProduct,
      products: productsWithoutNulls,
    })
    if (!product) return
    if (searchTitle) {
      setSearchInput(searchTitle)
      setSearchTerm(searchTitle)
    }
    setPaginationPage(1)
    const qty =
      suggestedProduct?.fromProgress && !pick?.typicalQuantity
        ? suggestedParQtyForProgressHandoff(product, pick)
        : suggestedParLevelEditValue(pick, product)
    startInlineEdit(product, 'parLevel', qty)
    window.setTimeout(() => {
      const input = document.querySelector('.gp-inline-edit-input')
      if (!input) return
      input.scrollIntoView({ block: 'center', behavior: 'smooth' })
      if (typeof input.focus === 'function') input.focus()
    }, 0)
  }
  const handleSetParsFromReadiness = () => {
    if (parEmpty.needsProductFirst) {
      const path = resolveParNeedProductPath(venueId)
      if (path) history.push(path)
      return
    }
    const product = pickProductToSetPar(productsWithoutNulls)
    if (!product) return
    const title = product.title || product.product?.title || ''
    if (title) {
      setSearchInput(title)
      setSearchTerm(title)
    }
    setPaginationPage(1)
    startInlineEdit(
      product,
      'parLevel',
      suggestedParLevelEditValue(null, product),
    )
    setParSetRowHint(true)
    window.setTimeout(() => {
      const input = document.querySelector('.gp-inline-edit-input')
      if (!input) return
      input.scrollIntoView({ block: 'center', behavior: 'smooth' })
      if (typeof input.focus === 'function') input.focus()
    }, 0)
  }
  const suggestedEmpty = bindGuidedEmptySuggestedActions({
    venueId,
    featureId: 'par_levels',
    suggested,
    onStart: beginFirstPar,
  })

  useEffect(() => {
    if (!suggestedProduct?.fromProgress) return
    if (progressParHandoffDoneRef.current) return
    if (isLoading || !productsWithoutNulls.length) return
    progressParHandoffDoneRef.current = true
    beginFirstPar()
  }, [
    suggestedProduct,
    isLoading,
    productsWithoutNulls.length,
    // beginFirstPar closes over products; run once per handoff
  ])

  return (
    <Wrapper>
      {shareList && (
        <ShareChangeWithGroup
          offer={{ kind: 'shoppingList', venueId }}
          onClose={() => setShareList(false)}
        />
      )}
      <FoodSafetyHeroBand>
        <FoodSafetyHeaderRow>
          <HeroCopy>
            <FoodSafetyPageTitle>
              Your venue&apos;s products
            </FoodSafetyPageTitle>
            <HeroIntro>
              <p>
                This is the shopping list — the products that should be bought.
                Catalogue is everything your suppliers currently make available.
              </p>
              <p>
                Add from Catalogue onto this list so you decide what colleagues
                can order. Turn off catalogue access in Your suppliers to lock
                that down. Missing something here? Search Catalogue first. If it
                is not there either, ask the supplier to update their product
                file on OMS — you may not have a complete file.
              </p>
            </HeroIntro>
            {parLevelUsageText && hasAnyPars ? (
              <Tooltip title={<ParLevelFieldTooltipContent />}>
                <ParLevelUsageLabel>
                  <strong>{parLevelUsageText}</strong>
                </ParLevelUsageLabel>
              </Tooltip>
            ) : null}
          </HeroCopy>
          <HeaderActions>
            <Button onClick={() => setShareList(true)}>
              Share with group…
            </Button>
            <Link to={`/v/${venueId}/suppliers`}>
              <Button type='primary'>Add supplier</Button>
            </Link>
          </HeaderActions>
        </FoodSafetyHeaderRow>
      </FoodSafetyHeroBand>
      <OllieOrderingEntry venueId={venueId} />
      <GrowthEngineProgressAskOllie
        featureId={suggestedProduct?.featureId || 'par_levels'}
        enabled={Boolean(suggestedProduct?.fromProgress) && !showParGuidedEmpty}
      />
      <GrowthEngineFollowUpStrip
        venueId={venueId}
        currentPlan={currentPlan}
        surface='shopping_lists'
      />
      <TipBanners>
        {hideFirstTryMarketing || bannerDismissed ? null : (
          <TipBanner data-testid='products-education-banner'>
            <TipContent>
              <TipHeadline>Tell staff what to order</TipHeadline>
              <TipText>
                These products are what staff see on the app — especially if
                catalogue browsing is off in Your Suppliers.
              </TipText>
              <WhyToggle
                type='link'
                onClick={() => setBannerExpanded(expanded => !expanded)}
              >
                {bannerExpanded ? 'Hide why this works' : 'Why this works'}
              </WhyToggle>
              {bannerExpanded ? (
                <WhyExpanded>
                  <p>
                    Keep control of what staff can order by only adding products
                    here. Favourite them to find them quickly, and order on
                    desktop with the basket icon.
                  </p>
                  <p>
                    Only products in this tab appear in Menu Builder. Add more
                    from Catalogue. {VENUE_PRODUCTS_BANNER_PAR_LEVEL_BULLET}
                  </p>
                </WhyExpanded>
              ) : null}
            </TipContent>
            <TipDismiss
              type='link'
              aria-label='Dismiss products tip'
              onClick={() => setBannerDismissed(true)}
            >
              <CloseOutlined />
            </TipDismiss>
          </TipBanner>
        )}
      </TipBanners>
      {parReadinessBanner ? (
        <ParReadinessBanner data-testid='stock-take-par-readiness-banner'>
          <ParReadinessCopy>
            <ParReadinessHeadline>
              {PAR_READINESS_OTP_HEADLINE}
            </ParReadinessHeadline>
            <ParReadinessBody>{PAR_READINESS_OTP_BODY}</ParReadinessBody>
            <span>{parReadinessBanner}</span>
          </ParReadinessCopy>
          <ParReadinessActions>
            <Button
              type='link'
              data-testid='stock-take-par-readiness-set-pars'
              onClick={handleSetParsFromReadiness}
            >
              {PAR_READINESS_SET_PARS_CTA}
            </Button>
            <Button
              type='primary'
              data-testid='stock-take-par-readiness-open-stock'
              onClick={() => history.push(`/v/${venueId}/stockTakes`)}
            >
              {PAR_READINESS_OTP_STOCK_CTA}
            </Button>
          </ParReadinessActions>
        </ParReadinessBanner>
      ) : null}
      {parSetRowHint ? (
        <ParSetRowHint data-testid='stock-take-par-row-hint'>
          {PAR_SET_PARS_ROW_HINT}
        </ParSetRowHint>
      ) : null}
      <ChangedPricesTable
        venueProducts={productsWithoutNulls}
        uniqueProductSuppliers={uniqueProductSuppliers}
        isOpen={false}
      />
      {isLoading ? (
        <Loading />
      ) : productsWithoutNulls.length === 0 &&
        !showParGuidedEmpty &&
        !isAdmin ? (
        <NoDataScreen page='venueProductsNoProduct' />
      ) : (
        <Fragment>
          {showFirstSuccessCompletion ? (
            <GrowthEngineFirstSuccessCompletion
              outcome={firstSuccessCompletion.outcome}
              nextSummary={firstSuccessCompletion.nextSummary}
              ctaLabel={firstSuccessCompletion.ctaLabel}
              managerOnePager={firstSuccessCompletion.managerOnePager}
              featureId='par_levels'
              onCta={() => {
                setJustAchievedFirstPar(false)
              }}
              onProgress={
                venueId
                  ? () => history.push(`/v/${venueId}/progress`)
                  : undefined
              }
            />
          ) : null}
          {showParGuidedEmpty ? (
            <GrowthEngineFeatureEmptyState
              featureId='par_levels'
              title={parEmpty.title}
              body={parEmpty.body}
              valueLine={parEmpty.valueLine}
              estimatedMinutes={parEmpty.estimatedMinutes}
              honestNote={parEmpty.honestNote}
              ctaLabel={parEmpty.label}
              onCta={suggestedEmpty.onCta}
              suggestedItems={
                parEmpty.needsProductFirst ? [] : suggestedEmpty.suggestedItems
              }
              onSuggestedItem={suggestedEmpty.onSuggestedItem}
              onSuggestedEdit={suggestedEmpty.onSuggestedEdit}
              onRejectSuggested={suggestedEmpty.onRejectSuggested}
              askOllieQuestion={
                suggestedProduct?.fromProgress
                  ? resolveProgressAskOllie(
                      suggestedProduct?.featureId || 'par_levels',
                    )?.question
                  : null
              }
              askOllieLabel={
                resolveProgressAskOllie(
                  suggestedProduct?.featureId || 'par_levels',
                )?.label
              }
            />
          ) : null}
          {hideProductsTableChrome ? null : (
            <>
              <ListCard>
                <DivContainer>
                  <ShoppingListToolbar>
                    <ShoppingListSearchInput
                      prefix={<SearchOutlined />}
                      placeholder='Search by title or SKU...'
                      size='large'
                      value={searchInput}
                      allowClear
                      onChange={event => {
                        const value = event.target.value
                        setSearchInput(value)
                        if (!value) applyShoppingListSearch('')
                      }}
                      onPressEnter={() => applyShoppingListSearch()}
                    />
                    {uniqueProductSuppliers.length > 0 && (
                      <ShoppingListFilterSelect
                        id='shopping-list-supplier-filter'
                        allowClear
                        showSearch
                        size='large'
                        placeholder='All suppliers'
                        value={selectedSupplierTitle || undefined}
                        optionFilterProp='children'
                        filterOption={(input, option) =>
                          String(option.props.children)
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        onChange={value => {
                          setSelectedSupplierTitle(value || '')
                          resetPagination()
                        }}
                      >
                        {uniqueProductSuppliers.map(title => (
                          <Option key={title} value={title}>
                            {title}
                          </Option>
                        ))}
                      </ShoppingListFilterSelect>
                    )}
                    <ShoppingListFilterSelect
                      id='shopping-list-type-filter'
                      allowClear
                      showSearch
                      size='large'
                      placeholder='All types'
                      value={selectedProductType || undefined}
                      optionFilterProp='children'
                      filterOption={(input, option) =>
                        String(option.props.children)
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      onChange={value => {
                        setSelectedProductType(value || '')
                        resetPagination()
                      }}
                    >
                      {uniqueProductTypes.map(type => (
                        <Option key={type} value={type}>
                          {type}
                        </Option>
                      ))}
                    </ShoppingListFilterSelect>
                    <Button
                      type='primary'
                      size='large'
                      onClick={() => applyShoppingListSearch()}
                    >
                      Search
                    </Button>
                    {shoppingListSearchActive ? (
                      <ShoppingListResultCount>
                        {searchProductsWithoutNulls.length}
                        {searchProductsWithoutNulls.length === 1
                          ? ' result'
                          : ' results'}
                        {shoppingListResultBits.length
                          ? ` · ${shoppingListResultBits.join(' · ')}`
                          : ''}
                      </ShoppingListResultCount>
                    ) : null}
                  </ShoppingListToolbar>
                  <ButtonsDiv>
                    <Button
                      data-testid='shopping-list-columns-button'
                      onClick={() => setColumnPickerOpen(true)}
                    >
                      <TableOutlined /> Edit columns
                    </Button>
                    {(isTFRAdmin || isAdmin || isJustAdmin || isGreenAdmin) && (
                      <Button
                        type={'danger'}
                        onClick={() => {
                          dispatch(deleteAllVenueProducts(venueId))
                        }}
                      >
                        Delete All
                      </Button>
                    )}
                    {products && (
                      <DownloadProductsCsv venueProducts={products} />
                    )}
                    <div>
                      <input
                        style={{
                          marginLeft: isMobile ? 0 : 25,
                          display: 'none',
                        }}
                        type='file'
                        ref={_file}
                        onChange={upload}
                      />
                      <Button
                        style={{
                          marginTop: isMobile ? 15 : 0,
                        }}
                        loading={isCSVLoading}
                        onClick={handleOnClickButton}
                      >
                        Upload CSV
                      </Button>
                    </div>
                  </ButtonsDiv>
                </DivContainer>
                <Table
                  scroll={RESPONSIVE_TABLE_SCROLL}
                  dataSource={searchProductsWithoutNulls.map(product => ({
                    key: product._id,
                    ...product,
                    zone: wordCaps(product.zone).trim(),
                    type: shoppingListProductTypeLabel(product),
                  }))}
                  pagination={{
                    position: 'both',
                    hideOnSinglePage: true,
                    current: paginationPage,
                    onChange: page => setPaginationPage(page),
                  }}
                  loading={isLoading}
                  rowClassName={record =>
                    getParLevelForProduct(record) > 0 ? 'par-level-set' : ''
                  }
                  columns={filterShoppingListVisibleColumns(
                    [
                      {
                        title: '',
                        dataIndex: 'product.cloudinaryImageId',
                        key: 'product.cloudinaryImageId',
                        render: (imageId, record) => {
                          // Venue's own photo overrides the supplier image
                          const displayImage =
                            getVenueCustomImageUrl(record) || imageId
                          if (!displayImage)
                            return (
                              <img
                                src={ComingImg}
                                width={60}
                                height={60}
                                alt=''
                              />
                            )
                          if (
                            displayImage.startsWith('https://') ||
                            displayImage.startsWith('http://')
                          ) {
                            return (
                              <img
                                src={displayImage}
                                width={60}
                                height={60}
                                style={{ borderRadius: 10, objectFit: 'cover' }}
                                alt=''
                              />
                            )
                          }
                          return <ProductImage imageId={displayImage} />
                        },
                      },
                      {
                        title: 'Product',
                        dataIndex: 'title',
                        key: 'title',
                        className: 'restrictShrink',
                        sorter: (a, b) =>
                          String(a.title)
                            .trim()
                            .localeCompare(String(b.title).trim()),
                        sortDirections: ['ascend', 'descend'],
                        render: (_, record) => (
                          <>
                            {renderInlineEditableCell(record, 'title')}
                            {record.isUnavailable ? (
                              <div
                                style={{
                                  color: '#8c8c8c',
                                  fontSize: 12,
                                  fontStyle: 'italic',
                                  marginTop: 4,
                                }}
                              >
                                Unavailable — removed from supplier catalogue
                              </div>
                            ) : null}
                          </>
                        ),
                      },
                      {
                        title: zoneColumnHeader,
                        key: 'zone',
                        className: 'restrictShrink',
                        filters: uniqueProductZones.map(zone => ({
                          text: zone,
                          value: zone,
                        })),
                        onFilter: (value, record) => record?.zone === value,
                        render: product => {
                          return (
                            <>
                              <div>{product.zone}</div>
                              {product.secondaryZone?.length > 0 && (
                                <StyledSecondaryZone>
                                  {product.secondaryZone}
                                </StyledSecondaryZone>
                              )}
                            </>
                          )
                        },
                      },
                      {
                        title: defaultQtyColumnHeader,
                        dataIndex: 'defaultQuantity',
                        key: 'defaultQuantity',
                        className: 'restrictShrink',
                        sorter: (a, b) => a.defaultQuantity - b.defaultQuantity,
                        sortDirections: ['ascend', 'descend'],
                        render: (_, record) =>
                          renderInlineEditableCell(record, 'defaultQuantity'),
                      },
                      {
                        title: parLevelColumnHeader,
                        dataIndex: 'parLevel',
                        key: 'parLevel',
                        className: 'parLevelColumn',
                        render: (_, record) =>
                          renderInlineEditableCell(record, 'parLevel'),
                        sorter: (a, b) =>
                          getParLevelForProduct(a) - getParLevelForProduct(b),
                        sortDirections: ['ascend', 'descend'],
                      },
                      {
                        title: excludeFromGpColumnHeader,
                        dataIndex: 'excludeFromGrossProfit',
                        key: 'excludeFromGrossProfit',
                        className: 'excludeFromGpColumn',
                        align: 'center',
                        width: 120,
                        filters: [
                          { text: 'Excluded', value: true },
                          { text: 'Included', value: false },
                        ],
                        onFilter: (value, record) =>
                          isVenueProductExcludedFromGrossProfit(record) ===
                          value,
                        sorter: (a, b) =>
                          Number(isVenueProductExcludedFromGrossProfit(a)) -
                          Number(isVenueProductExcludedFromGrossProfit(b)),
                        sortDirections: ['ascend', 'descend'],
                        // dataIndex makes render(text, record) — use the row, not the boolean.
                        render: (_, record) => (
                          <ExcludeFromGrossProfitTick
                            product={record}
                            products={products}
                            checked={isVenueProductExcludedFromGrossProfit(
                              record,
                            )}
                            onChange={async () => {
                              const confirmed =
                                await promptExcludeFromGrossProfitDuplicateTitle(
                                  {
                                    product: record,
                                    products,
                                    excluding:
                                      !isVenueProductExcludedFromGrossProfit(
                                        record,
                                      ),
                                  },
                                )
                              if (!confirmed) return

                              const result =
                                await persistVenueProductExcludeFromGrossProfitToggle(
                                  {
                                    dispatch,
                                    editVenueProduct,
                                    products,
                                    venueId,
                                    productId: record._id,
                                  },
                                )

                              if (!result.ok && result.error) {
                                RejectedNotification({
                                  message: 'Could not save product',
                                  description: result.error,
                                })
                              }
                            }}
                          />
                        ),
                      },
                      {
                        title: 'Size',
                        dataIndex: 'product.size',
                        key: 'product.size',
                        className: 'restrictShrink',
                        hideBelow: 1280,
                        sorter: (a, b) =>
                          String(a.size)
                            .trim()
                            .localeCompare(String(b.size).trim()),
                        sortDirections: ['ascend', 'descend'],
                      },
                      (!isPricesHidden && isAdmin) || !isAdmin
                        ? {
                            title: 'Price',
                            dataIndex: 'product.price',
                            key: 'price',
                            className: 'restrictShrink',
                            render: (text, item) => {
                              let diffSymbol = ''
                              if (
                                Object.prototype.hasOwnProperty.call(
                                  item.product,
                                  'priceHistory',
                                ) &&
                                item.product.priceHistory.length
                              ) {
                                const priceLastUpdated = dayjs(
                                  item.product.priceHistory[0].updatedAt,
                                )
                                const diff = dayjs().diff(
                                  priceLastUpdated,
                                  'days',
                                )
                                if (diff < 14) {
                                  diffSymbol = '▲'
                                  if (
                                    item.product.price <
                                    item.product.priceHistory[0].formerPrice
                                  ) {
                                    diffSymbol = '▼'
                                  }
                                }
                              }
                              return (
                                <span className='price-change'>
                                  {formatGbp(
                                    isAdmin ||
                                      isTFRAdmin ||
                                      isJustAdmin ||
                                      isGreenAdmin
                                      ? item?.brakesPrice &&
                                        !isNaN(item?.brakesPrice)
                                        ? item?.brakesPrice
                                        : text
                                      : !isNaN(item?.specialPrice) &&
                                        item?.specialPrice
                                      ? item?.specialPrice
                                      : item?.brakesPrice &&
                                        !isNaN(item?.brakesPrice)
                                      ? item?.brakesPrice
                                      : text,
                                  )}
                                  {item.product.hasVat ? ' + VAT' : ''}
                                  {diffSymbol && (
                                    <span className='diff-symbol'>
                                      {diffSymbol}
                                      <span className='tooltip'>
                                        Price has changed in the last 14 days
                                      </span>
                                    </span>
                                  )}
                                </span>
                              )
                            },
                            sorter: (a, b) =>
                              (a.product.hasVat
                                ? VAT_RATE * a.product.price
                                : a.product.price) -
                              (b.product.hasVat
                                ? VAT_RATE * b.product.price
                                : b.product.price),
                            sortDirections: ['ascend', 'descend'],
                          }
                        : {},
                      (isAdmin || isTFRAdmin || isJustAdmin || isGreenAdmin) &&
                      !isPricesHidden
                        ? {
                            title: 'Special price',
                            dataIndex: 'product.price',
                            key: 'specialPrice',
                            className: 'restrictShrink',
                            hideBelow: 1280,
                            render: (text, item) => {
                              let diffSymbol = ''
                              if (
                                Object.prototype.hasOwnProperty.call(
                                  item.product,
                                  'priceHistory',
                                ) &&
                                item.product.priceHistory.length
                              ) {
                                const priceLastUpdated = dayjs(
                                  item.product.priceHistory[0].updatedAt,
                                )
                                const diff = dayjs().diff(
                                  priceLastUpdated,
                                  'days',
                                )
                                if (diff < 14) {
                                  diffSymbol = '▲'
                                  if (
                                    item.product.price <
                                    item.product.priceHistory[0].formerPrice
                                  ) {
                                    diffSymbol = '▼'
                                  }
                                }
                              }
                              const specialPrice = getActiveSpecialPrice(item)
                              if (specialPrice == null) {
                                return <span className='price-change'>—</span>
                              }
                              return (
                                <span className='price-change'>
                                  {formatGbp(specialPrice)}
                                  {item.product.hasVat ? ' + VAT' : ''}
                                  {diffSymbol && (
                                    <span className='diff-symbol'>
                                      {diffSymbol}
                                      <span className='tooltip'>
                                        Price has changed in the last 14 days
                                      </span>
                                    </span>
                                  )}
                                </span>
                              )
                            },
                            sorter: (a, b) =>
                              (a.product.hasVat
                                ? VAT_RATE * a.product.price
                                : a.product.price) -
                              (b.product.hasVat
                                ? VAT_RATE * b.product.price
                                : b.product.price),
                            sortDirections: ['ascend', 'descend'],
                          }
                        : {},
                      {
                        title: 'Priced By',
                        dataIndex: 'product.pricedBy',
                        key: 'product.pricedBy',
                        className: 'restrictShrink',
                        hideBelow: 1280,
                        render: (_, venueProduct) => {
                          const { pricedBy, UoM } = venueProduct.product
                          return pricedBy
                            ? pricedBy === 'Each'
                              ? `${pricedBy}`
                              : `Per ${pricedBy}`
                            : UoM
                            ? UoM === 'Each'
                              ? `${UoM}`
                              : `Per ${UoM}`
                            : ''
                        },
                        sorter: (a, b) =>
                          String(a.size)
                            .trim()
                            .localeCompare(String(b.size).trim()),
                        sortDirections: ['ascend', 'descend'],
                      },
                      {
                        title: 'Supplier',
                        dataIndex: 'supplierTitle',
                        key: 'supplierTitle',
                        className: 'restrictShrink',
                        hideBelow: 1093,
                        filters: uniqueProductSuppliers.map(supplier => ({
                          text: supplier,
                          value: supplier,
                        })),
                        onFilter: (value, record) =>
                          record.supplierTitle === value,
                      },
                      {
                        title: 'SKU',
                        dataIndex: 'product.sku',
                        key: 'product.sku',
                        className: 'restrictShrink',
                        hideBelow: 1280,
                        sorter: (a, b) =>
                          String(a.product.sku)
                            .trim()
                            .localeCompare(String(b.product.sku).trim()),
                        sortDirections: ['ascend', 'descend'],
                      },
                      {
                        title: 'Nominal Code',
                        dataIndex: 'nominalCode',
                        key: 'nominalCode',
                        className: 'restrictShrink',
                        hideBelow: 1366,
                        render: (_, record) =>
                          renderInlineEditableCell(record, 'nominalCode'),
                        sorter: (a, b) =>
                          String(a.nominalCode || '')
                            .trim()
                            .localeCompare(String(b.nominalCode || '').trim()),
                        sortDirections: ['ascend', 'descend'],
                      },
                      dataplusIntegration
                        ? {
                            title: 'GLN',
                            dataIndex: 'product.GLN',
                            key: 'product.GLN',
                            className: 'restrictShrink',
                            hideBelow: 1366,
                            render: GLN => {
                              return GLN || ''
                            },
                          }
                        : {},
                      {
                        title: 'Type',
                        dataIndex: 'type',
                        key: 'type',
                        className: 'restrictShrink',
                        hideBelow: 1093,
                        filters: uniqueProductTypes.map(type => ({
                          text: type,
                          value: type,
                        })),
                        onFilter: (value, record) => record?.type === value,
                      },
                      {
                        title: 'Favourite',
                        key: 'Favourite',
                        filters: [{ text: 'favourite', value: 'favourite' }],
                        onFilter: (value, record) => {
                          return record.isFavourite
                        },

                        render: product => {
                          return (
                            <Checkbox
                              checked={product.isFavourite}
                              onChange={() => {
                                dispatch(
                                  editVenueProduct(
                                    buildVenueProductSavePayload(
                                      {
                                        ...product,
                                        isFavourite: !product.isFavourite,
                                      },
                                      venueId,
                                    ),
                                    { notifyFavourite: true },
                                  ),
                                )
                              }}
                            />
                          )
                        },
                      },
                      {
                        title: 'Action',
                        key: 'action',
                        className: 'restrictShrink sticky-actions-col',
                        onCell: () => ({ className: 'sticky-actions-cell' }),
                        render: (text, product) => {
                          return (
                            <StyledDiv>
                              <BasketFunctionality
                                order={product.product}
                                venueId={venueId}
                                venueProducts={products}
                              />
                              <StyledButton
                                type={'dashed'}
                                onClick={() => {
                                  setEditingProduct({
                                    ...product,
                                    parLevel: getParLevelForProduct(product),
                                    excludeFromGrossProfit:
                                      isVenueProductExcludedFromGrossProfit(
                                        product,
                                      ),
                                  })
                                }}
                              >
                                <EditOutlined />
                              </StyledButton>
                              {'  '}
                              <ReportProductIssueButton
                                venueId={venueId}
                                product={product.product || product}
                                supplierTitle={product.supplierTitle}
                                sourceScreen={
                                  PRODUCT_ISSUE_SOURCE.VENUE_PRODUCTS
                                }
                                iconOnly
                                type='dashed'
                                block
                                style={{ margin: '3px 3px 3px 0' }}
                              />
                              {'  '}
                              <Popconfirm
                                title='Are you sure to delete the product?'
                                placement='topRight'
                                onConfirm={() => {
                                  dispatch(deleteVenueProduct(product, venueId))
                                }}
                                okText='Yes'
                                cancelText='No'
                              >
                                <StyledButton type={'danger'}>
                                  <DeleteOutlined />
                                </StyledButton>
                              </Popconfirm>
                            </StyledDiv>
                          )
                        },
                      },
                    ],
                    {
                      hiddenKeys: hiddenColumnKeys,
                      windowWidth,
                    },
                  )}
                />
              </ListCard>

              <Modal
                title='Edit columns'
                visible={columnPickerOpen}
                onCancel={() => setColumnPickerOpen(false)}
                onOk={() => setColumnPickerOpen(false)}
                okText='Done'
                cancelButtonProps={{ style: { display: 'none' } }}
                data-testid='shopping-list-columns-modal'
              >
                <p style={{ marginTop: 0 }}>
                  Product and Action always stay visible. Tick the others you
                  want on this shopping list.
                </p>
                <ColumnPickerList>
                  {shoppingListPickerRows.map(column => (
                    <Checkbox
                      key={column.key}
                      checked={!pickerHiddenKeys.includes(column.key)}
                      onChange={() =>
                        handleToggleShoppingListColumn(column.key)
                      }
                    >
                      {column.label}
                    </Checkbox>
                  ))}
                </ColumnPickerList>
              </Modal>
              <Modal
                title='Edit Product'
                okText='Save'
                width={720}
                visible={editingProduct !== null}
                onOk={handleEditProductSave}
                onCancel={() => {
                  setEditingProduct(null)
                }}
              >
                {editingProduct && (
                  <EditVenueProductModal
                    product={editingProduct}
                    setEditedProduct={product => {
                      setEditingProduct(product)
                    }}
                  />
                )}
              </Modal>
            </>
          )}
        </Fragment>
      )}
    </Wrapper>
  )
}

const StyledDiv = styled.div`
  display: flex;
  flex-direction: column;
  width: 50px;
  align-items: stretch;

  /* Tooltip wraps the report button — keep it full width like Edit/Delete */
  > span {
    display: block;
    width: 100%;
  }
`

const ParLevelUsageLabel = styled.span`
  display: inline-block;
  margin-top: 0.35rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  font-size: 0.9rem;
  white-space: nowrap;
  cursor: help;
`

const HeroCopy = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  padding-right: ${({ theme }) => theme.spacing.md};
`

const HeroIntro = styled.div`
  margin: ${({ theme }) => theme.spacing.sm} 0 0;
  max-width: none;
  font-size: 0.95rem;
  line-height: 1.45;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};

  p {
    margin: 0 0 0.55rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
`

const ListCard = styled.div`
  width: 100%;
  margin: 0 0 ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  background: #fff;
  border: 1px solid ${FOOD_SAFETY_SURFACE.HERO_BORDER};
  border-radius: ${FOOD_SAFETY_SURFACE.CARD_RADIUS};
  box-shadow: ${FOOD_SAFETY_SURFACE.CARD_SHADOW};
  overflow-x: auto;
  box-sizing: border-box;

  .ant-table-thead > tr > th {
    word-break: keep-all;
    overflow-wrap: normal;
    hyphens: none;
    white-space: normal;
  }
`

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`

const Wrapper = styled(FadeIn)`
  width: 100%;
  max-width: none;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.sm};
  box-sizing: border-box;

  ${({ theme }) => theme.device('md')`
    padding: ${theme.spacing.md};
  `};

  ${ollieFabPageClearanceCss}
  ${antdTableHorizontalScroll}
  ${stickyActionsColumn}

  .ant-table-wrapper {
    padding-right: 0;
  }

  .sticky-actions-col,
  .sticky-actions-cell {
    min-width: 72px;
  }

  .selected {
    background: rgba(180, 216, 32, 0.33);
  }

  .par-level-set > td {
    background: rgba(180, 216, 32, 0.12) !important;
  }

  /* Sticky Actions cell must stay opaque — the par-level tint above is
     semi-transparent and would let scrolling text show through */
  .par-level-set > td.sticky-actions-cell {
    background: #f2f6e0 !important;
  }

  .restrictShrink {
    min-width: 95px;
  }

  .parLevelColumn {
    min-width: 8.5rem;
    white-space: nowrap;
  }

  .excludeFromGpColumn {
    min-width: 120px !important;
    width: 120px;
    padding-left: 10px;
    padding-right: 24px;
    text-align: center;
  }

  .diff-symbol {
    position: relative;
    font-size: 13px;
    margin-left: ${({ theme }) => theme.spacing.xxs};
    color: ${({ theme }) => theme.colours.panSilver};

    &:hover {
      .tooltip {
        opacity: 1;
        transform: translate(-50%, 0);
      }
    }
  }

  .tooltip {
    position: absolute;
    top: 100%;
    left: 50%;
    width: 150px;
    background: white;
    text-align: center;
    border: 1px solid ${({ theme }) => theme.colours.panSilver};
    border-radius: 5px;
    padding: ${({ theme }) => theme.spacing.xxs};
    color: black;
    z-index: 100;
    opacity: 0;
    pointer-events: none;
    transition: 200ms;
    transform: translate(-50%, 10px);
  }
`

const StyledButton = styled(Button)`
  margin: 3px 3px 3px 0;
`
const DivContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
`

const ShoppingListToolbar = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  flex: 1 1 520px;
  min-width: 0;
`

const ShoppingListSearchInput = styled(Input)`
  flex: 1 1 200px;
  min-width: 160px;
  max-width: 420px;
`

const ShoppingListFilterSelect = styled(Select)`
  flex: 0 1 200px;
  min-width: 150px;
  max-width: 260px;
`

const ShoppingListResultCount = styled.span`
  font-size: 14px;
  color: #595959;
  white-space: nowrap;
`
const ColumnPickerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const ButtonsDiv = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
`

const StyledSecondaryZone = styled.div`
  font-size: 14px;
  font-weight: bold;
  margin-top: 10px;
`

const ParSetRowHint = styled.p`
  margin: -8px 0 16px;
  padding: 0 16px;
  color: ${({ theme }) => theme?.colours?.oliveGrey || '#6b6b6b'};
  font-size: 13px;
  line-height: 1.4;
`

const ParReadinessBanner = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px 16px;
  width: 100%;
  margin: 0 0 ${({ theme }) => theme.spacing.md};
  padding: 1rem 1.1rem 1.15rem;
  border-radius: ${FOOD_SAFETY_SURFACE.CARD_RADIUS};
  border: 1px solid ${FOOD_SAFETY_SURFACE.HERO_BORDER};
  background: #fff;
  box-shadow: ${FOOD_SAFETY_SURFACE.CARD_SHADOW};
  color: ${({ theme }) => theme?.colours?.otherBlack || '#262626'};
  font-size: 14px;
  line-height: 1.4;
  box-sizing: border-box;
`

const ParReadinessCopy = styled.div`
  flex: 1 1 28rem;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const ParReadinessHeadline = styled.strong`
  font-size: 15px;
  font-weight: 600;
  line-height: 1.35;
`

const ParReadinessBody = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
`

const ParReadinessActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`

const TipBanners = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
  width: 100%;
`

const TipBanner = styled.div`
  width: 100%;
  padding: 1rem 1.1rem 1.15rem;
  border: 1px solid ${FOOD_SAFETY_SURFACE.HERO_BORDER};
  border-radius: ${FOOD_SAFETY_SURFACE.CARD_RADIUS};
  background: #fff;
  box-shadow: ${FOOD_SAFETY_SURFACE.CARD_SHADOW};
  display: flex;
  align-items: flex-start;
  gap: 8px;
  box-sizing: border-box;
`

const TipContent = styled.div`
  flex: 1;
  min-width: 0;
`

const TipHeadline = styled.p`
  margin: 0 0 0.45rem;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.3;
  color: ${({ theme }) => theme.colours.bbqBlack || '#000'};
`

const TipText = styled.p`
  margin: 0;
  max-width: 40rem;
  font-size: 0.9rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colours.bbqBlack || '#262626'};
`

const WhyToggle = styled(Button)`
  margin: 6px 0 0;
  padding: 0 !important;
  height: auto !important;
  font-size: 0.85rem !important;
  font-weight: 600;
  color: ${FOOD_SAFETY_SECONDARY_TEXT} !important;
  text-decoration: underline;

  &:hover,
  &:focus {
    color: ${({ theme }) => theme.colours.bbqBlack || '#000'} !important;
  }
`

const WhyExpanded = styled.div`
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid ${FOOD_SAFETY_SURFACE.HERO_BORDER};

  p {
    margin: 0 0 8px;
    max-width: 40rem;
    font-size: 0.85rem;
    line-height: 1.45;
    color: ${FOOD_SAFETY_SECONDARY_TEXT};

    &:last-child {
      margin-bottom: 0;
    }
  }
`

const TipDismiss = styled(Button)`
  color: ${FOOD_SAFETY_SECONDARY_TEXT} !important;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  padding: 0 !important;
  margin: 0;
  border: none;
  box-shadow: none;
  background: transparent !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;

  .anticon {
    font-size: 16px;
    color: ${FOOD_SAFETY_SECONDARY_TEXT};
  }
`

const GatedVenueProducts = props => {
  const blocked = useIsBlocked('shopping-lists')
  if (blocked) return <PermissionBlockScreen />
  return <VenueProducts {...props} />
}

export default GatedVenueProducts
